import React, { type ComponentProps, type PropsWithChildren } from 'react';
import { Platform, type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../hooks';
import { Container as GarnishContainer } from '../Container';
import { IconLink } from '../Icon';
import { ReCaptcha, type useReCaptcha } from '../ReCaptcha';

// ─────────────────────────────────────────────────────────────────────────────

// Doesn't scroll on its own, so consumers wrap it in their own scroll view.
export const Container = (props: Props) => {
  const {
    children,
    style,
    contentStyle,
    reCaptchaRef,
    reCaptchaMachine,
    onClose,
    onRecaptchaVerify,
    onRecaptchaError,
    removeSpacing = false,
  } = props;

  const { currentBreakpoint, minWidth } = useResponsive();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderCloseButton = currentBreakpoint.isXS && onClose !== undefined;
  const shouldRenderRecaptcha = reCaptchaRef !== undefined && onRecaptchaVerify !== undefined;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    currentBreakpoint.isXS ? undefined : styles.containerSM,
    contentStyle,
  ];
  const contentContainerStyles = [
    styles.contentContainer,
    currentBreakpoint.isXS ? undefined : styles.contentContainerSM,
    style,
  ];
  const recaptchaContainerStyles = [
    Platform.OS === 'web' ? styles.recaptchaContainerWeb : undefined,
    Platform.OS === 'web' && minWidth.isSM ? styles.recaptchaContainerWebSM : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <View style={contentContainerStyles}>
        {shouldRenderCloseButton ? (
          <GarnishContainer style={styles.closeButtonContainer}>
            <IconLink name="IconClose" onPress={onClose} />
          </GarnishContainer>
        ) : null}

        <GarnishContainer removeSpacing={removeSpacing} size="medium">
          {children}
        </GarnishContainer>

        {shouldRenderRecaptcha ? (
          <View style={recaptchaContainerStyles}>
            <ReCaptcha
              ref={reCaptchaRef}
              machine={reCaptchaMachine}
              onVerify={onRecaptchaVerify}
              onError={onRecaptchaError}
            />
          </View>
        ) : null}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerSM: {
    paddingVertical: theme.spacing['20'],
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: theme.spacing['6'],
  },
  contentContainerSM: {
    justifyContent: 'center',
  },
  closeButtonContainer: {
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'flex-end',
    marginBottom: theme.spacing['4'],
  },
  recaptchaContainerWeb: {
    marginTop: theme.spacing['6'],
  },
  recaptchaContainerWebSM: {
    height: 70,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  removeSpacing?: boolean;
  reCaptchaRef?: ReturnType<typeof useReCaptcha>['reCaptchaRef'];
  reCaptchaMachine?: ComponentProps<typeof ReCaptcha>['machine'];
  onClose?: () => void;
  onRecaptchaVerify?: ComponentProps<typeof ReCaptcha>['onVerify'];
  onRecaptchaError?: ComponentProps<typeof ReCaptcha>['onError'];
}>;
